import Container from "../../components/Container";
import PoolGrid from '../../components/PoolGrid'; // Adjust the import path as necessary
import clsx from "clsx";

import React, { useState, useMemo, memo, useEffect } from "react";
import { useStakingPoolInfos } from "../../hooks/useStakingPoolInfos";
import { useStakingDatas } from "../../hooks/useStakingDatas";
import { useStakingPoolDetail } from "../../hooks/useStakingPoolDetail";
import { LinearStakingData } from "../../entities/LinearStaking";

import { usePendingRewards } from "../../hooks/usePendingRewards";
import {
  formatDollarAmount,
  truncate,
} from "../../functions/bigNumber-util";
import Router from "next/router";
import { apiCoinGeckoGetPrice } from "../../constants/api.constant";
import { convertSecToDate, getNetworkByChainId } from "../../functions";
import {
  useGetMainNetwork,
} from "../../state/application/hooks";
import { Tab } from "../../components/Tabs";
import useActiveWeb3React from "../../hooks/useActiveWeb3React";
import ConfirmModal from "../../modals/Confirm";
import useLocalStorage from '../../hooks/useLocalStorage';
import { getTimeRemaining } from "../../utils/helper";
import { duration } from "moment";

const DEFAULT_CHAINID = Number(process.env.NEXT_PUBLIC_CHAINID);
const DEFAULT_TOKEN = process.env.NEXT_PUBLIC_TOKEN || '0x7979871595b80433183950ab6c6457752b585805';

export const poolName = (duration: string) => {
  let resultText = '';
  if (!duration) return resultText;
  const [result, isHours] = convertSecToDate(duration);
  return result === 0 && !isHours
    ? "No lock-up"
    : `${result} ${isHours ? "hour" : (result === 1 ? "day" : "days")} `;
};

export const poolDescription = (duration: string) => {

  const POOL_DETAILS = [
    {
      name: 'No lock-up',
      desc: [
        "Flexible APR Rewards: applies to the total asset amount you deposit in each Pool. Flexible APR is subject to change during the staking duration.",
        "Emergency Withdrawal: will have a much lower APR than the pool APR.",
        "Redemption: Your assets will be returned to your Wallet after a Delay Duration upon successful processing of redemption requests."
      ]
    },
    {
      name: '30 days',
      desc: [
        "Fixed APR Rewards: applies to the total asset amount you deposit in each Pool. Fixed APR is subject to change during the staking duration",
        "Emergency Withdrawal: will have a much lower APR than the pool APR.",
        "Redemption: Your assets will be returned to your Wallet after a Delay Duration upon successful processing of redemption requests."
      ]
    },
    {
      name: '90 days',
      desc: [
        "Fixed APR Rewards: applies to the total asset amount you deposit in each Pool. Fixed APR is subject to change during the staking duration",
        "Emergency Withdrawal: will have a much lower APR than the pool APR.",
        "Redemption: Your assets will be returned to your Wallet after a Delay Duration upon successful processing of redemption requests."
      ]
    },
    {
      name: '180 days',
      desc: [
        "Fixed APR Rewards: applies to the total asset amount you deposit in each Pool. Fixed APR is subject to change during the staking duration",
        "Emergency Withdrawal: will have a much lower APR than the pool APR.",
        "Redemption: Your assets will be returned to your Wallet after a Delay Duration upon successful processing of redemption requests."
      ]
    },
    {
      name: '360 days',
      desc: [
        "Fixed APR Rewards: applies to the total asset amount you deposit in each Pool. Fixed APR is subject to change during the staking duration",
        "Emergency Withdrawal: will have a much lower APR than the pool APR.",
        "Redemption: Your assets will be returned to your Wallet after a Delay Duration upon successful processing of redemption requests."
      ]
    }

  ]
  const poolNameValue = poolName(duration).trim();

  const poolDetail = POOL_DETAILS.find(pool => pool.name === poolNameValue);
  if (!poolDetail) {
    return `<ul style="list-style: unset;">${POOL_DETAILS[0].desc.map(item => `<li>${item}</li>`).join('')}</ul>`;
  }
  return `<ul style="list-style: unset;">${poolDetail.desc.map(item => `<li>${item}</li>`).join('')}</ul>`;
}


interface BalanceToken {
  bnbSECOND?: string;
}

export default memo(function Stake() {
  const chainId = useGetMainNetwork();
  const { account } = useActiveWeb3React();
  const poolList = useStakingPoolInfos();
  const [apr, setApr] = useState('15% ~ 90%');
  const useFetchPriceWithRetry = (localStorageKey) => {
    const [SECONDPrice, setSECONDPrice] = useState(() => {
      // Load initial price from localStorage, fallback to 0.02
      const savedPrice = localStorage.getItem(localStorageKey);
      return savedPrice ? parseFloat(savedPrice) : 0.02;
    });
    useEffect(() => {
      const fetchPriceWithRetry = async (retries = 3, delay = 1000) => {
        try {
          const response = await fetch(apiCoinGeckoGetPrice(DEFAULT_CHAINID), {
            method: "GET",
            headers: new Headers({
              "Content-Type": "application/json",
            }),
          });

          if (response.status === 429) {
            if (retries > 0) {
              setTimeout(() => {
                fetchPriceWithRetry(retries - 1, delay * 2);
              }, delay);
            } else {
              console.log("Too many requests. Please try again later.");
            }
            return;
          }

          if (response.status !== 200) {
            console.log(`Error: Status code ${response.status}`);
            return;
          }

          const data = await response.json();
          const address = DEFAULT_TOKEN;
          const price = data[address]?.usd || 0.0046;

          // Save price to state and localStorage
          setSECONDPrice(price);
          localStorage.setItem(localStorageKey, price.toString());
        } catch (error) {
          console.log("🚀 ~ file: index.tsx ~ line 85 ~ useEffect ~ error", error);
        }
      };

      fetchPriceWithRetry();
    }, [DEFAULT_CHAINID, apiCoinGeckoGetPrice, DEFAULT_TOKEN, localStorageKey]);

    return SECONDPrice;
  };

  const localStorageKey = 'SECONDPrice';

  const SECONDPrice = useFetchPriceWithRetry(localStorageKey);

  const totalStaked = useMemo(() => {
    return poolList.reduce(
      (staked, pool) => staked + Number(pool.totalStaked),
      0
    );
  }, [poolList]);

  const totalMyStakedPerPool = useMemo(() => {
    return poolList.map((pool) => {
      const poolStaked = pool?.linearStakingViews
        ?.filter(stakingView => stakingView?.status !== 2)
        .reduce((sum, stakingView) => sum + Number(stakingView.amount), 0) || 0;
      return poolStaked;
    });
  }, [poolList]);

  const processedPoolList = useMemo(() => {
    return poolList?.map((pool, idx) => ({
      id: idx,
      subName: "Stake SECOND - Earn SECOND",
      name: poolName(pool?.lockDuration),
      desc: poolDescription(pool?.lockDuration),
      ...pool,
      balance: totalMyStakedPerPool[idx], // Using the staked amount for each pool
      SECONDPrice: SECONDPrice
    }));
  }, [poolList, totalMyStakedPerPool, SECONDPrice]);

  const processedPoolListAvailable = useMemo(() => {
    const poolListAvailable = [...poolList]?.filter(item => {
      const distance = getTimeRemaining(item?.endJoinTime)
      if (distance > 0) {
        return true
      }
      return false
    })
    return poolListAvailable?.map((pool, idx) => ({
      id: idx,
      subName: "Stake SECOND - Earn SECOND",
      name: poolName(pool?.lockDuration),
      desc: poolDescription(pool?.lockDuration),
      ...pool,
      balance: totalMyStakedPerPool[idx], // Using the staked amount for each pool
      SECONDPrice: SECONDPrice
    }));
  }, [poolList, totalMyStakedPerPool, SECONDPrice]);

  const processedPoolListExpired = useMemo(() => {
    const poolListExpired = [...poolList]?.filter(item => {
      const distance = getTimeRemaining(item?.endJoinTime)
      if (distance < 0) {
        return true
      }
      return false
    })
    return poolListExpired?.map((pool, idx) => ({
      id: idx,
      subName: "Stake SECOND - Earn SECOND",
      name: poolName(pool?.lockDuration),
      desc: poolDescription(pool?.lockDuration),
      ...pool,
      balance: totalMyStakedPerPool[idx], // Using the staked amount for each pool
      SECONDPrice: SECONDPrice
    }));
  }, [poolList, totalMyStakedPerPool, SECONDPrice]);

  const handleOpenDetail = (id: any) => {
    Router.push(`/pool?id=${id}`);
  };

  const tabs: Tab[] = [
    {
      index: 0,
      name: "",
      content: (
        <PoolGrid
          onOpenDetail={(pool) => handleOpenDetail(pool.id)}
          pools={processedPoolListAvailable}
        />
      ),
      contentExpired: (
        <PoolGrid
          onOpenDetail={(pool) => handleOpenDetail(pool.id)}
          pools={processedPoolListExpired}
        />
      ),
    },

  ];

  const [openDetailNotification, setOpenDetailNotification] = useLocalStorage<boolean>('openDetailNotification', true);

  const [tabIndex, setTabIndex] = useState<number>(0);
  // const [openDetailNotification, setOpenDetailNotification] =
  //   useState<boolean>(false);

  const tabFocus = "";
  const [isToken, setIsToken] = useState<Boolean>(true);

  const switchSymbol = () => {
    setIsToken(!isToken);
  }

  return (
    <Container id="bar-page" className="py-20 " maxWidth="full">
      <div className="relative bg-black-blue__ rounded-2xl mx-auto w-90 lg:w-5/6 3xl:w-[73%] px-4 lg:px-5 py-10 flex flex-col w-full min-h-full ">
        <nav
          className={`absolute -top-5 px-5 rounded-xl bg-black-blue__ flex lg:flex-row`}
        >
          {tabs.map((item, idx) => (
            <button
              key={idx}
              onClick={() => {
                idx !== tabIndex && setTabIndex(idx);
              }}
              className={`py-2 px-4 block hover:zText-red-primary focus:outline-none ${idx === tabIndex ? tabFocus : " "
                }`}
            >
              {item.name}
            </button>
          ))}
        </nav>

        {tabIndex === 0 && (
          <div className={`w-full flex justify-center items-center py-8 ${tabIndex === 0 && "background-color-img"}`}>
            <div className="grid grid-cols-1 lg:grid-cols-3 text-center w-full gap-8">
              <div className="zBg-5 col-span-1 md-item rounded-[20px] py-6 mb-2 lg:mb-0 bg-gradient-total flex flex-col items-center zBorder-light">
                <div className="sub-title text-sm zText-2 pb-1 ">Total Staked</div>
                <div className="text-3xl font-bold flex justify-center items-center w-full flex-wrap">
                  <span>{isToken ? formatDollarAmount(totalStaked) : `$${formatDollarAmount(totalStaked * SECONDPrice)}`}</span>
                  <button className="ml-2 flex items-center zBg-3 zBorder-light zText-1 rounded px-3 py-1 text-sm text-right " onClick={switchSymbol}>
                    {`${isToken ? 'SECOND' : 'USD'}`} <span className="ml-1 text-[#FF2E29]">↔</span>
                  </button>
                </div>
              </div>
              <div className="zBg-5 col-span-1 md-item rounded-[20px] py-6 my-2  lg:my-0 bg-gradient-total flex flex-col items-center zBorder-light">
                <div className="sub-title text-sm zText-2 pb-1  justify-center flex-wrap">$SECOND Price</div>
                <div className="text-3xl font-bold ">
                  ${truncate(SECONDPrice, 4)}
                </div>
              </div>
              <div className="zBg-5 col-span-1 md-item rounded-[20px] py-6 mt-2 lg:mt-0 bg-gradient-total flex flex-col items-center zBorder-light">
                <div className="sub-title text-sm zText-2 pb-1">APR</div>
                <div className="text-3xl font-bold  justify-center flex-wrap">
                  {apr}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-col justify-center items-center mt-6">
          {processedPoolListAvailable?.length > 0 && (
            <>
              <div className="text-left w-full text-[28px] font-bold">Available</div>
              {tabs[tabIndex].content}
            </>
          )}
          {
            processedPoolListExpired?.length > 0 && (
              <>
                <div className="text-left w-full text-[28px] font-bold">Expired</div>
                {tabs[tabIndex].contentExpired}
              </>
            )
          }
        </div>
      </div>
      {/* {openDetailNotification && (
        <StakingCompetitionModal onClose={setOpenDetailNotification} />
      )} */}
    </Container>
  );
});

type StakingCompetitionModalProps = {
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
};
const StakingCompetitionModal = ({ onClose }: StakingCompetitionModalProps) => {
  return (
    <ConfirmModal
      toggleConfirmModal={() => onClose(false)}
      confirmModalOpen={true}
      title="Staking competition"
      maxWidth={700}
    >
      <div className="flex flex-col space-y-5 overflow-y-auto">
        <div className="items-center justify-between w-full py-3 px-8 rounded cursor-default zBg-4 text-[16px]">
          <div className="mb-3 text-justify">
            <span className="font-bold uppercase">Duration</span>: From 10 AM
            UTC 15.12.2021 to 10 AM UTC 15.01.2022
          </div>
          <div className="mb-3">
            <div>
              <span className="font-bold uppercase">Applied</span>: All staking
              into 60-day and 120-day locked pools (before 10AM UTC 15.01.2022)
            </div>
            <div className="text-justify">
              {`WHAT'S SPECIAL? If you have staked in a 60-day or 120-day
                    looked pool before today's announcement, this special APR
                    rate will also be automatically applied for the period
                    before 15.12.2021.`}
            </div>
          </div>
          <div className="mb-3">
            <div className="font-bold uppercase">Attractive APR:</div>
            <div>- 60-day locked pool: Increase to 18% APR</div>
            <div>- 120-day locked pool: Increase to 45% APR</div>
          </div>
          {/* <div className="mb-3">
            <div className="font-bold uppercase">Special bonus:</div>
            <div>- Top 3 highest stake will get 1 extra epic NFT card</div>
            <div>- Next top 20 wallet address will get a 20 USDT each</div>
          </div> */}

          <div className="text-justify">
            Deadline to claim reward: 10AM UTC 15.01.2022 (otherwise the APR
            will go back to the normal ones of 9% for 60-day pool and 20% for
            120-day pool and you will lose the extra APR)
          </div>
        </div>
      </div>
    </ConfirmModal>
  );
};
